import type { ReactNode } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { cn } from '@rocco/utils/cn';
import { FilterPillIcon } from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';
import { ProfessionalTabSearchField } from './ProfessionalTabSearchField';

interface ProfessionalTabListingBarProps {
    search: {
        value: string;
        onChange: (value: string) => void;
        placeholder: string;
        ariaLabel?: string;
    };
    /**
     * Opens the tab's filter sheet on the phone. Left off, the phone draws no
     * filter control: a tab with nothing to filter by offers nothing to press,
     * and the search field takes the row.
     */
    onOpenFilters?: () => void;
    /** The desktop filter drawer. Left off, the desktop draws no control. */
    filterDrawer?: { open: boolean; onToggle: () => void };
    /** Applied filters, shown on the filter control's corner. */
    filtersCount?: number;
    /** The desktop "Display: {n}" control, at the row's right end. */
    display?: ReactNode;
    className?: string;
}

/** The product directory's filter control, as it reads on the listing. */
const FILTER_BUTTON_CLASS =
    'relative flex shrink-0 items-center justify-center rounded-[1rem] bg-[#323232] text-white transition-colors';

const FilterCount = ({ count }: { count: number }) =>
    count > 0 ? (
        <span className="absolute -top-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full bg-white text-[0.6875rem] font-medium text-[#323232]">
            {count}
        </span>
    ) : null;

/**
 * The bar under the tabs on every listing tab — Products, Projects, Articles,
 * Videos, Network, Downloads: the product directory's filter control with its
 * search field beside it. The phone draws the field full width; the desktop
 * draws it at the directory's width and closes the row on Display.
 *
 * Which of the two it draws follows the display mode, not a CSS breakpoint:
 * the callers choose their filter wiring by display mode, and the mode and
 * `md` disagree on a phone held landscape and on a desktop window just under
 * 768px, where a breakpoint split would draw the control nobody wired.
 */
export const ProfessionalTabListingBar = ({
    search,
    onOpenFilters,
    filterDrawer,
    filtersCount = 0,
    display,
    className,
}: ProfessionalTabListingBarProps) => {
    const { desktop } = useAppDisplayModeContext();

    return (
        <div className={cn('flex items-center gap-3', className)}>
            {desktop ? (
                filterDrawer ? (
                    <button
                        type="button"
                        onClick={filterDrawer.onToggle}
                        aria-pressed={filterDrawer.open}
                        aria-label={
                            filterDrawer.open ? 'Hide Filters' : 'Show Filters'
                        }
                        className={cn(
                            FILTER_BUTTON_CLASS,
                            'size-10.5 hover:bg-[#484848] active:bg-[#515151]',
                        )}
                    >
                        <FilterPillIcon />
                        <FilterCount count={filtersCount} />
                    </button>
                ) : null
            ) : onOpenFilters ? (
                <button
                    type="button"
                    onClick={onOpenFilters}
                    aria-label="Refine"
                    className={cn(
                        FILTER_BUTTON_CLASS,
                        'size-11 active:bg-[#515151]',
                    )}
                >
                    <FilterPillIcon />
                    <FilterCount count={filtersCount} />
                </button>
            ) : null}
            <ProfessionalTabSearchField
                value={search.value}
                onChange={search.onChange}
                placeholder={search.placeholder}
                ariaLabel={search.ariaLabel}
                className={desktop ? 'w-[17.5rem] shrink-0' : 'min-w-0 flex-1'}
            />
            {desktop && display ? (
                <div className="ml-auto shrink-0">{display}</div>
            ) : null}
        </div>
    );
};
